import { useState, useEffect, useRef, useCallback } from 'react';
import { formatDate, stripHtml } from '../utils';
import ExportMenu from './ExportMenu';

// ─── FILMSTRIP TIMELINE COMPONENT ────────────────────────────────────────────
//
//  Renders events as cinematic film frames with Ken Burns effect, animated
//  transitions, ambient color sampling, thumbnail reel, and presentation mode.
//
//  Props:
//    events       {array}    event objects
//    colors       {object}   { primary, secondary, tertiary }
//    onEventClick {function} opens the event detail modal
//    canEdit      {boolean}  shows photo-prompt overlays in editor
//    onEditEvent  {function} opens event form (editor only)
//    transitionStyle {string}  'fade' | 'slide' | 'flash'  (default: 'fade')
//    autoPlaySpeed   {number}  ms per frame (default: 4000)
//

// ── Helpers ───────────────────────────────────────────────────────────────────

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Sample dominant color from an image URL using an offscreen canvas.
// Falls back to a preset palette index if sampling fails.
const AMBIENT_PALETTE = [
  '#4A8FD4','#D96B3B','#5AA35C','#9059A8','#D4A83B','#1A9E8C',
];

const sampleDominantColor = (src, idx, cb) => {
  if (!src || prefersReducedMotion()) { cb(AMBIENT_PALETTE[idx % AMBIENT_PALETTE.length]); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 32;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, 32, 32);
      const data = ctx.getImageData(0, 0, 32, 32).data;
      let r = 0, g = 0, b = 0, count = 0;
      for (let i = 0; i < data.length; i += 16) {
        r += data[i]; g += data[i + 1]; b += data[i + 2]; count++;
      }
      const avg = n => Math.round(n / count);
      cb(`rgb(${avg(r)},${avg(g)},${avg(b)})`);
    } catch { cb(AMBIENT_PALETTE[idx % AMBIENT_PALETTE.length]); }
  };
  img.onerror = () => cb(AMBIENT_PALETTE[idx % AMBIENT_PALETTE.length]);
  img.src = src;
};

// 5 gradient placeholders derived from palette + timeline colors
const PLACEHOLDER_GRADS = [
  (p, s) => `linear-gradient(135deg, ${p}cc, ${s}88)`,
  (p, s) => `linear-gradient(165deg, ${s}cc, ${p}55, #1a1a2e)`,
  (p, s) => `linear-gradient(120deg, #1a1a2e, ${p}99, ${s}66)`,
  (p, s) => `linear-gradient(200deg, ${s}99, #0d0d0d, ${p}77)`,
  (p, s) => `linear-gradient(150deg, ${p}66, #0d0d0d, ${s}99)`,
];

// Sprocket holes strip rendered as SVG
const SprocketStrip = ({ color, height = 22, count = 40 }) => (
  <div className="fs-sprocket" style={{ height, background: '#0d0d0d', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
    <svg width="100%" height={height} preserveAspectRatio="none">
      {Array.from({ length: count }).map((_, i) => (
        <rect
          key={i}
          x={i * (100 / count) + '%'}
          y="3"
          width={`${80 / count}%`}
          height={height - 6}
          rx="2"
          fill={color + '55'}
          stroke={color + '99'}
          strokeWidth="0.5"
        />
      ))}
    </svg>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const FilmstripTimeline = ({
  events,
  colors,
  onEventClick,
  canEdit = false,
  onEditEvent,
  transitionStyle: initTransStyle = 'fade',
  autoPlaySpeed: initSpeed = 4000,
  timeline = null,
  isEmbed = false,
}) => {
  const sorted = [...events].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));

  const [activeIdx,      setActiveIdx]      = useState(0);
  const [prevIdx,        setPrevIdx]        = useState(null);
  const [transitioning,  setTransitioning]  = useState(false);
  const [transDir,       setTransDir]       = useState(1); // 1=forward, -1=back
  const [descExpanded,   setDescExpanded]   = useState(false);
  const [autoPlaying,    setAutoPlaying]    = useState(false);
  const [progressPct,    setProgressPct]    = useState(0);
  const [ambientColor,   setAmbientColor]   = useState(colors.secondary);
  const [isFullscreen,   setIsFullscreen]   = useState(false);
  const [presentMode,    setPresentMode]    = useState(false);
  const [transStyle,     setTransStyle]     = useState(initTransStyle);
  const [autoSpeed,      setAutoSpeed]      = useState(initSpeed);
  const [kenBurnsKey,    setKenBurnsKey]    = useState(0); // reset Ken Burns on frame change

  const containerRef    = useRef(null);
  const reelRef         = useRef(null);
  const autoPlayRef     = useRef(null);
  const progressRef     = useRef(null);
  const progressStartRef = useRef(null);
  const reduced         = prefersReducedMotion();

  const TRANS_DURATION = reduced ? 0 : { fade: 400, slide: 300, flash: 150 }[transStyle] || 400;
  const total = sorted.length;

  // ── Navigate ─────────────────────────────────────────────────────────────

  const navigateTo = useCallback((idx, dir = 1) => {
    if (idx === activeIdx || transitioning) return;
    setTransDir(dir);
    setPrevIdx(activeIdx);
    setTransitioning(true);
    setDescExpanded(false);
    setProgressPct(0);
    if (progressStartRef.current) clearTimeout(progressStartRef.current);
    setTimeout(() => {
      setActiveIdx(idx);
      setTransitioning(false);
      setPrevIdx(null);
      setKenBurnsKey(k => k + 1);
    }, TRANS_DURATION);
  }, [activeIdx, transitioning, TRANS_DURATION]);

  const goNext = useCallback(() => navigateTo((activeIdx + 1) % total, 1),  [navigateTo, activeIdx, total]);
  const goPrev = useCallback(() => navigateTo((activeIdx - 1 + total) % total, -1), [navigateTo, activeIdx, total]);

  // ── Auto-play ─────────────────────────────────────────────────────────────

  const stopAutoPlay = useCallback(() => {
    clearInterval(autoPlayRef.current);
    clearInterval(progressRef.current);
    autoPlayRef.current = null;
    progressRef.current = null;
    setProgressPct(0);
  }, []);

  const startAutoPlay = useCallback(() => {
    stopAutoPlay();
    setProgressPct(0);
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgressPct(Math.min(100, (elapsed / autoSpeed) * 100));
    }, 50);
    autoPlayRef.current = setInterval(() => {
      setProgressPct(0);
      progressStartRef.current = setTimeout(() => {
        const startTime2 = Date.now();
        clearInterval(progressRef.current);
        progressRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime2;
          setProgressPct(Math.min(100, (elapsed / autoSpeed) * 100));
        }, 50);
      }, 50);
      setActiveIdx(prev => {
        const next = (prev + 1) % total;
        setTransDir(1);
        setPrevIdx(prev);
        setTransitioning(true);
        setDescExpanded(false);
        setKenBurnsKey(k => k + 1);
        setTimeout(() => { setTransitioning(false); setPrevIdx(null); }, TRANS_DURATION);
        return next;
      });
    }, autoSpeed);
  }, [stopAutoPlay, autoSpeed, total, TRANS_DURATION]);

  const toggleAutoPlay = useCallback(() => {
    if (autoPlaying) { setAutoPlaying(false); stopAutoPlay(); }
    else             { setAutoPlaying(true);  startAutoPlay(); }
  }, [autoPlaying, startAutoPlay, stopAutoPlay]);

  useEffect(() => { if (autoPlaying) startAutoPlay(); }, [autoSpeed]);
  useEffect(() => () => stopAutoPlay(), []);

  // ── Ambient color ──────────────────────────────────────────────────────────

  useEffect(() => {
    const ev = sorted[activeIdx];
    sampleDominantColor(ev?.photo, activeIdx, c => setAmbientColor(c));
  }, [activeIdx]);

  // ── Thumbnail scroll sync ──────────────────────────────────────────────────

  useEffect(() => {
    if (!reelRef.current) return;
    const el = reelRef.current.querySelector(`[data-thumb="${activeIdx}"]`);
    if (el) el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIdx]);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== containerRef.current) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev(); }
      if (e.key === 'Home')       { e.preventDefault(); navigateTo(0, -1); }
      if (e.key === 'End')        { e.preventDefault(); navigateTo(total - 1, 1); }
      if (e.key === 'Escape' && presentMode) exitPresent();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, navigateTo, total, presentMode]);

  // ── Touch swipe ───────────────────────────────────────────────────────────

  const touchStart = useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) { dx < 0 ? goNext() : goPrev(); }
    touchStart.current = null;
  };

  // ── Fullscreen ────────────────────────────────────────────────────────────

  const enterFullscreen = () => {
    containerRef.current?.requestFullscreen?.();
    setIsFullscreen(true);
  };
  const exitFullscreen = () => {
    document.exitFullscreen?.();
    setIsFullscreen(false);
  };
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ── Presentation mode ─────────────────────────────────────────────────────

  const startPresent = () => {
    enterFullscreen();
    setPresentMode(true);
    setAutoPlaying(true);
    startAutoPlay();
  };
  const exitPresent = () => {
    exitFullscreen();
    setPresentMode(false);
    setAutoPlaying(false);
    stopAutoPlay();
  };

  // ── Empty guard (after all hooks) ─────────────────────────────────────────

  if (!sorted.length) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
      No events yet. Add your first event.
    </div>
  );

  const ev = sorted[activeIdx];
  const prevEv = prevIdx !== null ? sorted[prevIdx] : null;
  const desc = stripHtml(ev.description || '');
  const fullDesc = desc;
  const shortDesc = desc.slice(0, 180);
  const frameNum = String(activeIdx + 1).padStart(2, '0');
  const totalNum = String(total).padStart(2, '0');
  const hasSources = ev.sources?.length > 0;
  const gradBg = PLACEHOLDER_GRADS[activeIdx % PLACEHOLDER_GRADS.length](colors.primary, colors.secondary);

  // ── Transition class ──────────────────────────────────────────────────────

  const transClass = reduced || !transitioning ? '' :
    transStyle === 'slide' ? (transDir > 0 ? 'fs-slide-in-r' : 'fs-slide-in-l') :
    transStyle === 'flash' ? 'fs-flash-in' :
    'fs-fade-in';

  const prevTransClass = reduced || !transitioning ? '' :
    transStyle === 'slide' ? (transDir > 0 ? 'fs-slide-out-l' : 'fs-slide-out-r') :
    transStyle === 'flash' ? 'fs-flash-out' :
    'fs-fade-out';

  const HOLE_COUNT = 24;
  const THUMB_COUNT_VISIBLE = 8;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={containerRef}
      className={`fs-root${isFullscreen ? ' fs-fullscreen' : ''}${presentMode ? ' fs-present' : ''}`}
      tabIndex={0}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{ '--fs-ambient': ambientColor, '--fs-primary': colors.primary, '--fs-secondary': colors.secondary }}
    >
      {/* ── Export toolbar ── */}
      {!isEmbed && !presentMode && timeline && (
        <div className="no-export" style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 12px 0', position: 'relative', zIndex: 10 }}>
          <ExportMenu timeline={timeline} captureRef={containerRef} formats={['pdf', 'embed']} />
        </div>
      )}

      {/* ── Top sprocket strip ── */}
      <SprocketStrip color={ambientColor} count={HOLE_COUNT} height={22} />

      {/* ── Central stage ── */}
      <div className="fs-stage">

        {/* Previous frame (for slide/fade transitions) */}
        {prevEv && transitioning && (
          <div className={`fs-frame fs-frame-prev ${prevTransClass}`} aria-hidden="true">
            {prevEv.photo
              ? <div className="fs-bg-photo" style={{ backgroundImage: `url(${prevEv.photo})` }} />
              : <div className="fs-bg-grad" style={{ background: PLACEHOLDER_GRADS[prevIdx % PLACEHOLDER_GRADS.length](colors.primary, colors.secondary) }} />
            }
          </div>
        )}

        {/* Active frame */}
        <div className={`fs-frame fs-frame-active ${transClass}`}>

          {/* Background */}
          {ev.photo ? (
            <div
              key={`kb-${kenBurnsKey}`}
              className={`fs-bg-photo${reduced ? '' : ' fs-ken-burns'}`}
              style={{ backgroundImage: `url(${ev.photo})` }}
            />
          ) : (
            <div className="fs-bg-grad" style={{ background: gradBg }} />
          )}

          {/* Film grain overlay */}
          {!reduced && <div className="fs-grain" aria-hidden="true" />}

          {/* Gradient overlay (bottom) */}
          <div className="fs-grad-overlay" aria-hidden="true" />

          {/* ── Upper left: date badge ── */}
          <div className="fs-date-badge" style={{ background: ambientColor }}>
            {formatDate(ev.startDate, ev.startEra !== 'NONE')}
          </div>

          {/* ── Upper right: frame counter ── */}
          <div className="fs-counter">
            {frameNum} / {totalNum}
          </div>

          {/* ── Lower content ── */}
          <div className="fs-content">
            <h2 className="fs-title">{ev.title}</h2>
            {desc && (
              <p className={`fs-desc${descExpanded ? ' fs-desc-expanded' : ''}`}>
                {descExpanded ? fullDesc : shortDesc}
                {!descExpanded && fullDesc.length > 180 && '…'}
              </p>
            )}
            <div className="fs-actions">
              {fullDesc.length > 180 && (
                <button
                  className="fs-action-btn"
                  onClick={() => setDescExpanded(v => !v)}
                  style={{ borderColor: colors.primary, color: colors.primary }}
                >
                  {descExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}
              <button
                className="fs-action-btn"
                onClick={() => onEventClick(ev)}
                style={{ borderColor: colors.primary, color: colors.primary }}
              >
                View Details
              </button>
            </div>
          </div>

          {/* ── Lower right: sources badge ── */}
          {hasSources && (
            <button
              className="fs-sources-badge"
              onClick={() => onEventClick(ev)}
              title="View sources"
              style={{ background: ambientColor + 'cc' }}
            >
              ¹
            </button>
          )}

          {/* ── Photo prompt (editor only, no photo) ── */}
          {canEdit && !ev.photo && onEditEvent && (
            <button
              className="fs-photo-prompt"
              onClick={() => onEditEvent(ev)}
              title="Add a photo to this event"
            >
              <span className="fs-photo-prompt-icon">📷</span>
              <span className="fs-photo-prompt-label">Add Photo</span>
            </button>
          )}

          {/* ── Nav arrows ── */}
          {!presentMode && (
            <>
              <button
                className="fs-nav-btn fs-nav-prev"
                onClick={goPrev}
                aria-label="Previous frame"
                style={{ '--fs-nav-color': ambientColor }}
              >
                ‹
              </button>
              <button
                className="fs-nav-btn fs-nav-next"
                onClick={goNext}
                aria-label="Next frame"
                style={{ '--fs-nav-color': ambientColor }}
              >
                ›
              </button>
            </>
          )}

          {/* ── Cinematic controls (bottom center) ── */}
          {!presentMode && (
            <div className="fs-cinematic-bar">
              <button
                className="fs-cin-btn"
                onClick={toggleAutoPlay}
                title={autoPlaying ? 'Pause' : 'Play'}
                style={{ background: autoPlaying ? ambientColor + '33' : 'transparent', borderColor: ambientColor, color: ambientColor }}
              >
                {autoPlaying ? '⏸' : '▶'}
              </button>
              <button
                className="fs-cin-btn"
                onClick={startPresent}
                title="Presentation mode"
                style={{ borderColor: ambientColor, color: ambientColor }}
              >
                ⊞
              </button>
              <button
                className="fs-cin-btn"
                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                style={{ borderColor: ambientColor, color: ambientColor }}
              >
                {isFullscreen ? '⊟' : '⛶'}
              </button>
            </div>
          )}

          {/* Presentation mode: just pause button */}
          {presentMode && (
            <div className="fs-present-bar">
              <button
                className="fs-cin-btn"
                onClick={toggleAutoPlay}
                style={{ background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(255,255,255,0.5)', color: '#fff', fontSize: 20 }}
              >
                {autoPlaying ? '⏸' : '▶'}
              </button>
              <button
                className="fs-cin-btn"
                onClick={exitPresent}
                style={{ background: 'rgba(0,0,0,0.5)', borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}
              >
                ✕ Exit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom sprocket strip ── */}
      <SprocketStrip color={ambientColor} count={HOLE_COUNT} height={22} />

      {/* ── Thumbnail reel (hidden in present mode) ── */}
      {!presentMode && (
        <div className="fs-reel-wrap">
          {/* Reel top perforations */}
          <div className="fs-reel-holes">
            {Array.from({ length: HOLE_COUNT }).map((_, i) => (
              <div key={i} className="fs-reel-hole" style={{ borderColor: ambientColor + '55', background: ambientColor + '22' }} />
            ))}
          </div>

          {/* Thumbnails */}
          <div className="fs-reel" ref={reelRef}>
            {sorted.map((e, i) => {
              const isActive = i === activeIdx;
              const tGrad = PLACEHOLDER_GRADS[i % PLACEHOLDER_GRADS.length](colors.primary, colors.secondary);
              return (
                <button
                  key={e.id}
                  data-thumb={i}
                  className={`fs-thumb${isActive ? ' fs-thumb-active' : ''}`}
                  onClick={() => navigateTo(i, i > activeIdx ? 1 : -1)}
                  aria-label={`Go to frame ${i + 1}: ${e.title}`}
                  aria-pressed={isActive}
                  style={isActive ? { borderColor: colors.primary, '--fs-primary': colors.primary } : {}}
                >
                  {e.photo
                    ? <img src={e.photo} alt="" className="fs-thumb-img" loading={Math.abs(i - activeIdx) <= 1 ? 'eager' : 'lazy'} />
                    : <div className="fs-thumb-grad" style={{ background: tGrad }} />
                  }
                  {/* Active progress bar */}
                  {isActive && autoPlaying && (
                    <div className="fs-thumb-progress" style={{ width: `${progressPct}%`, background: colors.primary }} />
                  )}
                  <div className="fs-thumb-label">{e.title}</div>
                  <div className="fs-thumb-date">{formatDate(e.startDate, e.startEra !== 'NONE')}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Progress dots ── */}
      <div className="fs-dots" role="tablist" aria-label="Frame navigation">
        {sorted.map((e, i) => {
          const isActive = i === activeIdx;
          return (
            <button
              key={e.id}
              role="tab"
              aria-selected={isActive}
              aria-label={`Frame ${i + 1}: ${e.title}`}
              className={`fs-dot${isActive ? ' fs-dot-active' : ''}`}
              onClick={() => navigateTo(i, i > activeIdx ? 1 : -1)}
              style={isActive ? { background: ambientColor, borderColor: ambientColor, transform: 'scale(1.4)' } : { borderColor: colors.secondary + '66' }}
            />
          );
        })}
      </div>

      {/* ── Hint ── */}
      {!presentMode && (
        <div className="fs-hint">
          ← → to navigate · Space to play · click frame to open
        </div>
      )}
    </div>
  );
};

// ── Design tab settings sub-component ────────────────────────────────────────
// Exported separately so TimelineEditor can render it in the Design tab.

export const FilmstripSettings = ({ timeline, onUpdate }) => {
  const ts = timeline.filmstripTransition || 'fade';
  const sp = timeline.filmstripSpeed     || 4000;

  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Filmstrip Settings
      </p>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, display: 'block' }}>Transition Style</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['fade', 'Fade'], ['slide', 'Slide'], ['flash', 'Flash']].map(([val, lbl]) => (
            <button
              key={val}
              type="button"
              aria-pressed={ts === val}
              onClick={() => onUpdate({ filmstripTransition: val })}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 'var(--radius)',
                border: `1px solid ${ts === val ? 'var(--accent)' : 'var(--border2)'}`,
                background: ts === val ? 'var(--accent)22' : 'transparent',
                color: ts === val ? 'var(--accent)' : 'var(--text2)',
                cursor: 'pointer', fontSize: 13,
              }}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, display: 'block' }}>Auto-play Speed</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['6000', 'Slow'], ['4000', 'Normal'], ['2000', 'Fast']].map(([val, lbl]) => (
            <button
              key={val}
              type="button"
              aria-pressed={sp === Number(val)}
              onClick={() => onUpdate({ filmstripSpeed: Number(val) })}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 'var(--radius)',
                border: `1px solid ${sp === Number(val) ? 'var(--accent)' : 'var(--border2)'}`,
                background: sp === Number(val) ? 'var(--accent)22' : 'transparent',
                color: sp === Number(val) ? 'var(--accent)' : 'var(--text2)',
                cursor: 'pointer', fontSize: 13,
              }}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilmstripTimeline;
